import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MaterialModule } from '@app/material.module';
import { LoggerService } from '@app/logger/logger.service';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { ServicePackagesService } from '@app/shared/services/entities/servicePackages.service';
import { ProductsService } from '@app/shared/services/entities/products.service';
import { ProcedureService } from '@app/shared/services/entities/procedure.service';

const log = new LoggerService('CONTRACT-PACKAGES COMPONENT');

/**
 * Paquetes de servicio del contrato.
 *
 * Cada paquete es la lista de material de un procedimiento. Se guarda en la
 * entidad `servicepackages` (tabla propia, NO jsonb del contrato) y el
 * asistente de intervenciones lo usa en el paso 3 para sugerir el paquete
 * que mejor cubre el material cargado.
 *
 * v1: cada paquete lleva UNA configuración (DEFAULT). El modelo ya soporta
 * varias (cementada / no cementada); cuando haga falta la UI, solo se
 * agrega el selector.
 */
@Component({
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, MaterialModule],
  selector: 'app-contract-packages',
  templateUrl: './contract-packages.component.html',
  styleUrls: ['./contract-packages.component.scss'],
})
export class ContractPackagesComponent implements OnInit {
  public contractData: any;
  public permissions: { [key: string]: any } = {};

  public packagesList: any[] = [];
  public productList: any[] = [];
  public procedureList: any[] = [];

  public selectedPackage: any = null; // null = ninguno; {} nuevo
  public packageForm!: FormGroup;
  public items: any[] = [];

  public productFilter = '';

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<ContractPackagesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private customMessageService: CustomMessageService,
    private servicePackagesService: ServicePackagesService,
    private productsService: ProductsService,
    private proceduresService: ProcedureService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.contractData = { ...this.data['contract'] };
    this.permissions = { ...(this.data['permissions'] ?? {}) };
    this.packageForm = this.formBuilder.group({
      name: [null, Validators.required],
      code: [null],
      procedure: [null],
      description: [null],
      active: [true],
    });
    this.customMessageService.open({ title: 'Espere', type: 'neutral', message: 'Cargando paquetes...', loading: true });
    try {
      await Promise.all([this.getPackages(), this.getProducts(), this.getProcedures()]);
    } catch (err) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    } finally {
      this.customMessageService.close();
    }
  }

  private async getPackages(): Promise<void> {
    const apiResponse = await this.servicePackagesService.search({
      searchType: 'default', condition: { deleted: false, contract: this.contractData.id },
    });
    this.packagesList = ((apiResponse.data as any[]) ?? [])
      .sort((a, b) => String(a.name).localeCompare(String(b.name), 'es'));
  }

  private async getProducts(): Promise<void> {
    const apiResponse = await this.productsService.search({
      searchType: 'default', condition: { deleted: false, contract: this.contractData.id },
    });
    this.productList = ((apiResponse.data as any[]) ?? [])
      .sort((a, b) => String(a.code).localeCompare(String(b.code), 'es'));
  }

  private async getProcedures(): Promise<void> {
    const apiResponse = await this.proceduresService.search({ searchType: 'default', condition: { deleted: false } });
    this.procedureList = ((apiResponse.data as any[]) ?? [])
      .sort((a, b) => String(a.name).localeCompare(String(b.name), 'es'));
  }

  public get filteredProducts(): any[] {
    const f = this.productFilter.trim().toLowerCase();
    if (!f) return this.productList.slice(0, 50);
    return this.productList
      .filter(p => String(p.code ?? '').toLowerCase().includes(f) || String(p.description ?? '').toLowerCase().includes(f))
      .slice(0, 50);
  }

  public itemsOf(pkg: any): number {
    return (pkg?.configurations?.[0]?.items ?? []).length;
  }

  // ─── Edición ─────────────────────────────────────────────────────────────

  newPackage(): void {
    this.selectedPackage = {};
    this.items = [];
    this.packageForm.reset({ active: true });
  }

  editPackage(pkg: any): void {
    this.selectedPackage = pkg;
    this.items = structuredClone(pkg.configurations?.[0]?.items ?? []);
    this.packageForm.setValue({
      name: pkg.name ?? null,
      code: pkg.code ?? null,
      procedure: pkg.procedure ?? null,
      description: pkg.description ?? null,
      active: pkg.active ?? true,
    });
  }

  addItem(product: any): void {
    const existing = this.items.find(i => i.product === product.id);
    if (existing) { existing.quantity = (Number(existing.quantity) || 0) + 1; return; }
    this.items.push({
      product: product.id,
      code: product.code,
      description: product.description,
      quantity: 1,
      optional: false,
      notes: null,
    });
  }

  removeItem(index: number): void {
    this.items.splice(index, 1);
  }

  async savePackage(): Promise<void> {
    if (this.packageForm.invalid) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'El paquete necesita al menos un nombre.', dismissBtn: true });
      return;
    }
    if (this.items.length === 0) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Agrega al menos una partida al paquete.', dismissBtn: true });
      return;
    }
    const cleanItems = this.items.map(i => ({ ...i, quantity: Math.max(1, Number(i.quantity) || 1) }));
    const payload: any = {
      ...this.packageForm.value,
      contract: this.contractData.id,
      configurations: [{ code: 'DEFAULT', name: 'Única', description: null, items: cleanItems }],
    };
    this.customMessageService.open({ title: 'Espere', type: 'neutral', message: 'Guardando paquete...', loading: true });
    try {
      let apiResponse: any;
      if (this.selectedPackage?.id) {
        apiResponse = await this.servicePackagesService.update({ id: this.selectedPackage.id, ...payload });
      } else {
        apiResponse = await this.servicePackagesService.create(payload);
      }
      this.customMessageService.close();
      if (apiResponse.state === 'success') {
        await this.customMessageService.open({ title: '¡Hecho!', type: 'success', message: 'Paquete guardado.', duration: 1500 });
        this.selectedPackage = null;
        await this.getPackages();
      }
    } catch (err) {
      this.customMessageService.close();
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  async deletePackage(pkg: any): Promise<void> {
    const response = await this.customMessageService.open({
      title: 'Cuidado', type: 'warning',
      message: `¿Quitar el paquete <b>${pkg.name}</b> del contrato?`,
      dismissBtn: true, acceptBtn: true,
    });
    if (!response) return;
    try {
      await this.servicePackagesService.update({ id: pkg.id, deleted: true });
      if (this.selectedPackage?.id === pkg.id) this.selectedPackage = null;
      await this.getPackages();
    } catch (err) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  procedureName(id: string): string {
    return this.procedureList.find(p => p.id === id)?.name ?? '—';
  }

  close(): void {
    this.dialogRef.close(true);
  }
}
